/*  src/pages/AnalyticsDashboard.jsx  – side-by-side charts + CSV */
import React, { useEffect, useState } from 'react';
import API      from '../services/api';
import Sidebar  from '../components/AnalyticsSidebar';

import { format, startOfDay, subDays, addDays, eachDayOfInterval } from 'date-fns';



import {
  Chart, CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, ArcElement, Tooltip, Legend,
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';

/* ─── ChartJS setup ─────────────────────────────────────────── */
Chart.register(
  CategoryScale, LinearScale, PointElement,
  LineElement,   BarElement,  ArcElement,
  Tooltip,       Legend
);

/* ─── styles ────────────────────────────────────────────────── */
import '../styles/Dashboard.css';
import './_analytics.css';           // defines .insight-box, .grid-auto, …

/* ─── constants ─────────────────────────────────────────────── */
const todayStart = startOfDay(new Date());
const PRESETS = {
  // 00:00 today  →  00:00 tomorrow  (end is *exclusive* - that’s OK)
  today : { from: todayStart,             to: addDays(todayStart, 1) },

  // the other presets piggy-back on todayStart
  week  : { from: subDays(todayStart, 6), to: addDays(todayStart, 1) },
  month : { from: subDays(todayStart,29), to: addDays(todayStart, 1) },
 };

/* ─── tiny KPI box component ───────────────────────────────── */
const TinyBox = ({ clr, txt, val }) => (
  <div className="kpi" style={{ background: clr }}>
    <h3>{val}</h3>
    <p>{txt}</p>
  </div>
);

/* =================================================================== */
export default function AnalyticsDashboard() {
  /* raw data pulls --------------------------------------------------- */
  const [students, setStudents] = useState([]);
  const [guests  , setGuests]   = useState([]);

  useEffect(() => {
    (async () => {
      /*  token is injected automatically by axios interceptor  */
      const [s, g] = await Promise.all([
        API.get('/requests/admin'),
        API.get('/guests')
      ]);
      setStudents(s.data ?? []);
      setGuests  (g.data ?? []);
    })();
  }, []);

  /* date filter state ------------------------------------------------ */
  const [mode , setMode ] = useState('month');
  const [range, setRange] = useState(PRESETS.month);

  const pickPreset = k => { setMode(k); setRange(PRESETS[k]); };
  const pickCustom = (which, v) => {
    const dt = v ? new Date(v) : today;
    setRange(r => ({ ...r, [which]: dt }));
    setMode('range');
  };

  /* build day buckets ------------------------------------------------ */
  const days   = eachDayOfInterval({ start: range.from, end: range.to });
  const labels = days.map(d => format(d, 'dd-MMM'));
  const arr0   = () => Array(days.length).fill(0);

  /* student buckets (OUT | IN | PENDING) */
  const studDay  = { out: arr0(), in: arr0(), pending: arr0() };
  students.forEach(s => {
    const i = days.findIndex(
      d => format(d, 'yyyy-MM-dd') === format(s.dateTime, 'yyyy-MM-dd')
    );
    if (i < 0) return;
    if (s.status === 'out')      studDay.out[i]     += 1;
    if (s.status === 'in')       studDay.in[i]      += 1;
    if (s.status === 'approved') studDay.pending[i] += 1;   // treat “approved” as pending
  });

  /* guest buckets (IN | PENDING | OUT) */
  const guestDay = { in: arr0(), pending: arr0(), out: arr0() };
  guests.forEach(g => {
    const i = days.findIndex(
      d => format(d, 'yyyy-MM-dd') === format(g.createdAt, 'yyyy-MM-dd')
    );
    if (i < 0) return;
    if (g.status === 'in')      guestDay.in[i]      += 1;
    if (g.status === 'pending') guestDay.pending[i] += 1;
    if (g.status === 'out')     guestDay.out[i]     += 1;
  });

  /* KPI tiles -------------------------------------------------------- */
  const totStud  = {
    pen: students.filter(s => s.status === 'approved').length,
    out: students.filter(s => s.status === 'out').length,
    in : students.filter(s => s.status === 'in').length
  };
  const totGuest = {
    pen: guests.filter(g => g.status === 'pending').length,
    out: guests.filter(g => g.status === 'out').length,
    in : guests.filter(g => g.status === 'in').length
  };

  /* chart datasets --------------------------------------------------- */
  const dsStudLine = {
    labels,
    datasets: [
      { label:'OUT',      data: studDay.out,     borderColor:'#f97316', tension:.3 },
      { label:'IN',       data: studDay.in,      borderColor:'#10b981', tension:.3 },
      { label:'PENDING',  data: studDay.pending, borderColor:'#facc15', tension:.3 }
    ]
  };

  const dsGuestBar = {
    labels,
    datasets: [
      { label:'IN',       data: guestDay.in,      backgroundColor:'#4ade80' },
      { label:'PENDING',  data: guestDay.pending, backgroundColor:'#facc15' },
      { label:'OUT',      data: guestDay.out,     backgroundColor:'#f97316' }
    ]
  };

  const pieStud  = {
    labels:['Pending','Out','In'],
    datasets:[{ data:[totStud.pen, totStud.out, totStud.in],
      backgroundColor:['#facc15','#f97316','#10b981'] }]
  };
  const pieGuest = {
    labels:['Pending','Out','In'],
    datasets:[{ data:[totGuest.pen, totGuest.out, totGuest.in],
      backgroundColor:['#facc15','#f97316','#4ade80'] }]
  };

  /* CSV helper ------------------------------------------------------- */
  const csvDownload = (arr, name) => {
    if (!arr.length) return alert('No data to export.');
    const rows = [Object.keys(arr[0])].concat(arr.map(o => Object.values(o)));
    const csv  = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `${name}_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ───────────────────────────────────────────────── RENDER ──────── */
  return (
    <div className="split-layout">
      <Sidebar />

      <section className="analytics-wrapper">

        {/* date toolbar */}
        <div className="lookback">
          {['today','week','month'].map(p => (
            <button key={p}
              className={mode === p ? 'active' : ''}
              onClick={() => pickPreset(p)}>
              {p.toUpperCase()}
            </button>
          ))}
          <span className="custom-pick">
            <input type="date"
              value={format(range.from,'yyyy-MM-dd')}
              onChange={e => pickCustom('from', e.target.value)} />
            &nbsp;→&nbsp;
            <input type="date"
              value={format(range.to,'yyyy-MM-dd')}
              onChange={e => pickCustom('to', e.target.value)} />
          </span>
        </div>

        {/* ==== STUDENT INSIGHTS ==== */}
        <div className="insight-box">
          <div className="headline">
            <h3>🎓 Students Leave Flow</h3>
            <button className="csv-btn"
              onClick={() => csvDownload(students, 'students')}>⬇︎ CSV</button>
          </div>

          <div className="kpi-row">
            <TinyBox clr="#facc15" txt="Pending" val={totStud.pen}/>
            <TinyBox clr="#f97316" txt="Out"     val={totStud.out}/>
            <TinyBox clr="#10b981" txt="In"      val={totStud.in}/>
          </div>

          <div className="grid-auto">
            <div className="chart-card"><Line data={dsStudLine}/></div>
            <div className="chart-card"><Pie  data={pieStud   }/></div>
          </div>
        </div>

        {/* ==== GUEST INSIGHTS ==== */}
        <div className="insight-box">
          <div className="headline">
            <h3>🧑‍💼 Guests Visit Flow</h3>
            <button className="csv-btn"
              onClick={() => csvDownload(guests, 'guests')}>⬇︎ CSV</button>
          </div>

          <div className="kpi-row">
            <TinyBox clr="#facc15" txt="Pending" val={totGuest.pen}/>
            <TinyBox clr="#f97316" txt="Out"     val={totGuest.out}/>
            <TinyBox clr="#4ade80" txt="In"      val={totGuest.in}/>
          </div>

          <div className="grid-auto">
            <div className="chart-card"><Bar data={dsGuestBar}/></div>
            <div className="chart-card"><Pie data={pieGuest}/></div>
          </div>
        </div>

      </section>
    </div>
  );
}
